import { TrendingUp, ShieldCheck, FileX } from 'lucide-react';

const metrics = [
  {
    icon: TrendingUp,
    title: 'Zero-Friction Efficiency',
    body: 'Surgeons dictate once; LINK handles the reconciliation.',
  },
  {
    icon: ShieldCheck,
    title: 'Defensible Gains',
    body: 'Every dollar recovered is backed by a "Triple-Match" audit trail.',
  },
  {
    icon: FileX,
    title: 'Reduced Denial Rates',
    body: 'Stop the "Write-Off" cycle by aligning authorization and documentation pre-export.',
  },
];

const ROIAchievementSection = () => {
  return (
    <section className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header — centered */}
        <div className="text-center mb-14 md:mb-20 max-w-2xl mx-auto">
          <span className="text-xs font-medium text-primary tracking-wide uppercase">Proven Revenue Impact</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-foreground tracking-tight mt-3 mb-5">
            Unlocking <span className="text-primary">13%</span>–<span className="text-primary">28%</span> Revenue Recovery.
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            LINK identifies and captures earned revenue that traditional RCM systems miss,
            all while maintaining the highest level of regulatory compliance.
          </p>
        </div>

        {/* Growth Bar Visual */}
        <div className="mb-16 md:mb-20">
          <div className="bg-card border border-border rounded-md p-6 sm:p-8 md:p-10">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <div>
                <p className="text-xs font-mono text-muted-foreground tracking-widest uppercase mb-1">
                  Procedure-Specific Revenue
                </p>
                <p className="text-sm text-muted-foreground">Before vs. After LINK Implementation</p>
              </div>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm bg-muted-foreground/30" />
                  Traditional RCM
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm bg-primary" />
                  With LINK
                </span>
              </div>
            </div>

            <div className="space-y-6">
              {[
                { label: 'Orthopedic Procedures', before: 72, after: 95, lift: '28%' },
                { label: 'Sports Medicine', before: 78, after: 94, lift: '21%' },
                { label: 'Joint Reconstruction', before: 80, after: 93, lift: '16%' },
                { label: 'Arthroscopic Surgery', before: 83, after: 96, lift: '13%' },
              ].map((row) => (
                <div key={row.label} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">{row.label}</span>
                    <span className="text-xs font-mono font-bold text-primary">+{row.lift} recovery</span>
                  </div>
                  <div className="relative h-3 w-full rounded bg-muted overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 rounded bg-muted-foreground/20"
                      style={{ width: `${row.before}%` }}
                    />
                    <div
                      className="absolute inset-y-0 left-0 rounded bg-primary"
                      style={{ width: `${row.after}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Cards — left-aligned content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {metrics.map((metric) => (
            <div
              key={metric.title}
              className="bg-card border border-border rounded-md p-8 h-full transition-all hover:shadow-lg"
            >
              <div className="w-11 h-11 rounded-md bg-primary/10 border border-primary/15 flex items-center justify-center mb-5">
                <metric.icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-3">{metric.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{metric.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ROIAchievementSection;
